import { useState, KeyboardEvent, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Flex,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Box,
  Button,
  Spinner,
  InputGroup,
  Input,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
  Select,
} from '@chakra-ui/react';

import {
  AddIcon,
  SearchIcon,
  SmallCloseIcon,
  RepeatIcon,
} from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';
import Card from 'components/card/Card';

import { apiGetCarriers } from 'api';

import { useUrlFilters } from 'hooks/useUrlFilters';

import { PROVINCE_OPTIONS } from 'variables/options';

import type { APIPaginatedResponse, APICarrierData } from 'types/api.types';

const TABLE_HEADERS = [
  'Carrier Name',
  'Acturis Products',
  'Accepts Dash',
  'PAF Template',
  'Address',
  'CSIO Code',
];

const PROVINCE_LABELS: Record<number, string> = PROVINCE_OPTIONS.reduce(
  (acc, { value, label }) => ({ ...acc, [value]: label }),
  {},
);

const CARRIER_FILTER_KEYS = ['name', 'is_dash'] as const;

const CarrierListView = () => {
  const navigate = useNavigate();
  const { setFilters, page, setPage, resetAll, queryString, searchParams } =
    useUrlFilters(CARRIER_FILTER_KEYS);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBgColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [nameSearch, setNameSearch] = useState('');
  const [isDashSearch, setIsDashSearch] = useState('');

  useEffect(() => {
    setNameSearch(searchParams.get('name') ?? '');
    setIsDashSearch(searchParams.get('is_dash') ?? '');
  }, [searchParams]);

  const { error, data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['carrier-list', page, queryString],
    queryFn: () => apiGetCarriers(page + 1, queryString),
  }) as {
    data: APIPaginatedResponse<APICarrierData>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
    refetch: () => void;
  };

  const handleEditCarrier = (carrierData: APICarrierData) => {
    navigate(`/admin/settings/carriers/edit/${carrierData.id}`);
  };

  const handleCreateCarrier = () => {
    navigate('/admin/settings/carriers/new');
  };

  const handleSearch = () => {
    setFilters({
      name: nameSearch.trim(),
      is_dash: isDashSearch.trim(),
    });
  };

  const resetSearch = () => {
    setNameSearch('');
    setIsDashSearch('');
    resetAll();
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  if (error) {
    return (
      <Card>
        <Box p="20px" pb="0">
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{JSON.stringify(error)}</AlertDescription>
          </Alert>
        </Box>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Carriers
        </Text>
        <Flex gap={2}>
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
          <Tooltip label="Refresh carriers" hasArrow>
            <IconButton
              aria-label="Refresh carriers"
              icon={<RepeatIcon />}
              onClick={refetch}
              variant="ghost"
              isLoading={isLoading || isFetching}
            />
          </Tooltip>
          <Button
            leftIcon={<AddIcon />}
            variant="brand"
            size="md"
            onClick={handleCreateCarrier}
          >
            New Carrier
          </Button>
        </Flex>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <InputGroup>
                <Input
                  variant="main"
                  placeholder="Search by name"
                  value={nameSearch}
                  onChange={(e) => setNameSearch(e.target.value)}
                  bg={inputBg}
                  size="md"
                  onKeyDown={handleKeyDown}
                />
              </InputGroup>
            </Box>

            <Box flex="1">
              <Select
                variant="main"
                placeholder="Select DASH support"
                value={isDashSearch}
                onChange={(e) => setIsDashSearch(e.target.value)}
                bg={inputBg}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="true">Accepts DASH</option>
                <option value="false">Does't accept DASH</option>
              </Select>
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Table variant="simple" color="gray.500">
        <Thead>
          <Tr>
            {TABLE_HEADERS.map((header) => (
              <Th
                key={header}
                pe="10px"
                borderColor={borderColor}
                color="gray.400"
              >
                {header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={TABLE_HEADERS.length} textAlign="center">
                <Flex align="center" justify="center" mt="20px">
                  <Spinner size="sm" mr="10px" /> Loading carriers...
                </Flex>
              </Td>
            </Tr>
          )}

          {data?.results.map((carrier) => (
            <Tr
              key={carrier.id}
              cursor="pointer"
              _hover={{
                bg: hoverBgColor,
              }}
              onClick={() => handleEditCarrier(carrier)}
            >
              <Td>
                <Flex align="center">
                  <Text color={textColor} fontSize="sm" fontWeight="700">
                    {carrier.name}
                  </Text>
                </Flex>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {carrier.province_configs?.length || 0}
                </Text>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {carrier.is_dash ? 'Yes' : 'No'}
                </Text>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {carrier.paf_template || '-'}
                </Text>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {[
                    carrier.address,
                    carrier.city,
                    carrier.postal_code,
                    carrier.province ? PROVINCE_LABELS[carrier.province] : null,
                  ]
                    .filter(Boolean)
                    .join(', ') || '-'}
                </Text>
              </Td>
              <Td>
                <Text color={textColor} fontSize="sm">
                  {carrier.csio_code || '-'}
                </Text>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No carriers found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default CarrierListView;
